import { send } from '../../api/modules/apiV2';
import type {
  RewardsProfile,
  CatalogueSku,
  SkuDetail,
  ReserveResult,
  RedeemResult,
  RewardClaim,
  RestockScheduleEntry,
  ActivityResponse,
} from './types';
import type { IRewardsService } from './IRewardsService';

const STORE_PREFIX = 'realtime/rewards/store';
const PROFILE_PREFIX = 'realtime/rewards';

export class RewardsService implements IRewardsService {
  async getProfile(): Promise<RewardsProfile> {
    return send('get', `${PROFILE_PREFIX}/profile`);
  }

  async getCatalogue(): Promise<CatalogueSku[]> {
    return send('get', `${STORE_PREFIX}/catalogue`);
  }

  async getSkuDetail(skuId: string): Promise<SkuDetail> {
    return send('get', `${STORE_PREFIX}/sku/${skuId}`);
  }

  async reserve(skuId: string, clientRequestId: string): Promise<ReserveResult> {
    return send('post', `${STORE_PREFIX}/reserve`, { skuId, clientRequestId });
  }

  async confirm(claimId: string): Promise<RedeemResult> {
    return send('post', `${STORE_PREFIX}/confirm`, { claimId });
  }

  async cancel(claimId: string): Promise<void> {
    return send('post', `${STORE_PREFIX}/cancel`, { claimId });
  }

  async getClaims(): Promise<RewardClaim[]> {
    const res = await send('get', `${STORE_PREFIX}/claims?limit=100`);
    if (res && 'success' in res && !res.success) return [];
    return Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
  }

  async getRestockSchedule(): Promise<{ schedule: RestockScheduleEntry[] }> {
    return send('get', `${STORE_PREFIX}/restock-schedule`);
  }

  async getActivity(limit = 50, offset = 0): Promise<ActivityResponse> {
    return send('get', `${PROFILE_PREFIX}/activity?limit=${limit}&offset=${offset}`);
  }

  async getHostMessages(): Promise<{ text: string; tiers?: string[] }[]> {
    const res = await send('get', `${PROFILE_PREFIX}/host-messages`);
    const raw = Array.isArray(res?.messages) ? res.messages : [];
    return raw.map((m: unknown) =>
      typeof m === 'string' ? { text: m } : (m as { text: string; tiers?: string[] }),
    );
  }

  async getRebootIncentive(): Promise<{ amount: number } | null> {
    const res = await send('get', `${PROFILE_PREFIX}/reboot-incentive`);
    return res?.incentive ?? null;
  }

  async fireAction(actionType: string, machineId: string, payload: Record<string, unknown>): Promise<unknown> {
    return send('post', `${PROFILE_PREFIX}/actions/fire`, { actionType, machineId, payload });
  }
}
